import { useEffect, useRef, useState, type FC } from "react";
import { useUser } from "@/shared/hooks/use-user";
import { forgetConversation, useAssistantState } from "../model/assistantStore";
import { abortAssistantTurn } from "../model/sendAssistantTurn";
import { useAssistantProfile } from "../model/useAssistantAvailable";
import { AssistantBubble } from "./AssistantBubble";
import { AssistantPanel } from "./AssistantPanel";

/** Полноэкранный режим плеера: пузырь не должен висеть поверх видео. */
function useFullscreen(): boolean {
  const [fullscreen, setFullscreen] = useState(false);
  useEffect(() => {
    const update = () => setFullscreen(Boolean(document.fullscreenElement));
    update();
    document.addEventListener("fullscreenchange", update);
    return () => document.removeEventListener("fullscreenchange", update);
  }, []);
  return fullscreen;
}

/**
 * Лента принадлежит пользователю: вход, выход или смена аккаунта её
 * сбрасывают, и панель при следующем открытии грузит ленту заново. Живёт в
 * виджете, а не в панели: панель размонтирована, пока закрыта.
 */
function useResetOnUserChange() {
  const { user } = useUser();
  const userId = user?.id ?? null;
  const previousUserId = useRef(userId);
  useEffect(() => {
    if (previousUserId.current === userId) return;
    previousUserId.current = userId;
    abortAssistantTurn();
    forgetConversation();
  }, [userId]);
}

/**
 * Виджет помощника витрины: пузырь и панель. Подключается в _app через
 * next/dynamic без SSR, внутри гейта 18+. Если помощник у платформы
 * выключен (platform.assistant = null), ничего не рендерит.
 */
export const AssistantWidget: FC = () => {
  const profile = useAssistantProfile();
  const { open } = useAssistantState();
  const fullscreen = useFullscreen();
  useResetOnUserChange();
  if (!profile) return null;

  return (
    <>
      <AssistantBubble name={profile.name} hidden={open || fullscreen} />
      <AssistantPanel name={profile.name} avatarUrl={profile.avatarUrl} />
    </>
  );
};
